import { useRef } from "react";
import Box from "@amzn/meridian/esm/components/box";
import ProgressTracker, { ProgressTrackerStep } from "@amzn/meridian/esm/components/progress-tracker";
import Row from "@amzn/meridian/esm/components/row";
import Theme from "@amzn/meridian/esm/components/theme";
import noAgenda from "../../../images/no_agenda.svg";
import AgendaRowLabel from "./AgendaRowLabel";
import AgendaEditInput from "./AgendaEditInput";
import AddButton from "../AddButton";
import EmptyState from "../EmptyState";
import Loading from "../../../components/Loading";
import ErrorState from "../ErrorState";
import { cx } from "@emotion/css";
import { hideAddConnectorClass, hideAddMarkerClass, trackerClipClass } from "./agenda.styles";
import { useAgendaActions, useAgendaItems } from "./agenda.hooks";
import {
  AGENDA_STATE_TO_STEP_TYPE,
  ADD_AGENDA_LABEL,
  EMPTY_AGENDA_MESSAGE,
  AGENDA_TRACKER_TOKENS,
  STEP_GAP_SPACER_SIZE,
  ROW_LINE_HEIGHT,
  ROW_SPACING_INSET,
} from "./agenda.constants";
import { tabBodyClass, tabScrollClass } from "../insights.styles";
import { scrollToBottom } from "../insights.utils";
import { useSession } from "../../../context/SessionContext";

/** Agenda view for an active session: a ProgressTracker with inline add/edit/delete. */
const Agenda = ({ insightStore }) => {
  const { meetingUuid } = useSession();
  const { items, isLoading, error } = useAgendaItems(meetingUuid, insightStore);
  const scrollRef = useRef(null);
  const { add, edit, del, highlightId } = useAgendaActions({ meetingUuid, insightStore, scrollRef });

  const handleEditKeyDown = (itemId) => (e) => {
    if (e.key === "Enter") edit.save(itemId);
    else if (e.key === "Escape") edit.cancel(itemId);
  };

  const handleAddKeyDown = (e) => {
    if (e.key === "Enter") add.save();
    else if (e.key === "Escape") add.cancel();
  };

  // Scroll an edit input fully into view — the browser's native focus-scroll may not
  // fully reveal it inside the flex scroll container. "nearest" so editing a row mid-list
  // doesn't jump the view.
  const revealInput = (e) => {
    requestAnimationFrame(() => e?.target?.scrollIntoView?.({ block: "nearest" }));
  };

  // The add row renders at the bottom of the tracker; reveal it when it opens.
  const revealAddForm = () => scrollToBottom(scrollRef);

  const renderStepLabel = (item) => {
    if (edit.ids.has(item.id)) {
      return (
        <AgendaEditInput
          value={edit.values[item.id] ?? ""}
          onChange={(val) => edit.setValue(item.id, val)}
          onKeyDown={handleEditKeyDown(item.id)}
          onFocus={revealInput}
          commitLabel="Save"
          onCommit={() => edit.save(item.id)}
          onCancel={() => edit.cancel(item.id)}
          spacingInset={ROW_SPACING_INSET}
        />
      );
    }

    return (
      <AgendaRowLabel
        item={item}
        highlighted={highlightId === item.id}
        onEdit={() => edit.start(item)}
        onDelete={() => del.start(item)}
        confirming={del.pendingId === item.id}
        onConfirmDelete={() => del.confirm(item)}
        onCancelDelete={del.cancel}
        spacingInset={ROW_SPACING_INSET}
      />
    );
  };

  const addInput = (
    <AgendaEditInput
      value={add.value}
      onChange={add.setValue}
      onKeyDown={handleAddKeyDown}
      onFocus={revealAddForm}
      placeholder={ADD_AGENDA_LABEL}
      commitLabel="Add"
      onCommit={add.save}
      onCancel={add.cancel}
      spacingInset={ROW_SPACING_INSET}
    />
  );

  // Closed state of the add row: the "+ Add agenda" trigger, rendered as the add step's label so
  // it occupies the exact slot the input will take.
  const addTrigger = (
    <Row spacing="none" alignmentVertical="center" height={ROW_LINE_HEIGHT}>
      <AddButton label={ADD_AGENDA_LABEL} onClick={add.start} />
    </Row>
  );

  if (isLoading) return <Loading />;

  if (error) return <ErrorState message={error} />;

  if (items.length === 0 && !add.open) {
    return (
      <EmptyState
        image={noAgenda}
        message={EMPTY_AGENDA_MESSAGE}
        action={<AddButton label={ADD_AGENDA_LABEL} variant="primary" onClick={add.start} />}
      />
    );
  }

  return (
    <Theme tokens={AGENDA_TRACKER_TOKENS}>
      <div className={tabBodyClass}>
        <div ref={scrollRef} className={tabScrollClass}>
          <Box spacingInset="300 400 350 450">
            {/* hideAddConnectorClass always applies; the marker is only hidden while the add row
                shows its trigger rather than its input. */}
            <div className={cx(trackerClipClass, hideAddConnectorClass, !add.open && hideAddMarkerClass)}>
              <ProgressTracker direction="column" type="theme" compact autoTrackLength>
                {items.map((item) => (
                  <ProgressTrackerStep
                    key={item.id}
                    id={item.id}
                    type={AGENDA_STATE_TO_STEP_TYPE[item.state] || "future"}
                    showLabel
                    label={renderStepLabel(item)}
                  >
                    <Box height={STEP_GAP_SPACER_SIZE} />
                  </ProgressTrackerStep>
                ))}
                {/* Add row is ALWAYS the last step, so opening it never mounts/unmounts anything
                    (which is what shifted the list). Its label swaps in place: the "+ Add agenda"
                    trigger when closed, the text input when open. No child — the last step has no
                    track, so a spacer here would be a no-op. */}
                <ProgressTrackerStep
                  key="add-step"
                  id="add-step"
                  type="future"
                  showLabel
                  label={add.open ? addInput : addTrigger}
                />
              </ProgressTracker>
            </div>
          </Box>
        </div>
      </div>
    </Theme>
  );
};

export default Agenda;
